import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Send, MessageSquare, HelpCircle } from "lucide-react";
import { toast } from "sonner";

export default function TutorChat({ mode = "chat" }) {
  // mode = "chat" (full answers) or "homework" (Socratic hints)
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [opts, setOpts] = useState({ grades: [], subjects: [] });
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/api/tutor/profile").then(({ data }) => {
      if (!data.profile.onboarded) { nav("/tutor/onboarding"); return; }
      setProfile(data.profile);
      setOpts(data.options);
      setGrade(data.profile.grade);
      setSubject(data.profile.subjects[0] || "Mathematics");
    }).catch(() => nav("/login"));
    // run-on-mount: api/setters are stable references
  }, [nav]);

  useEffect(() => {
    setMessages([]); setSessionId(null);
    // reset chat state when switching mode; setters are stable
  }, [mode]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // scroll-to-bottom on new message; scrollRef is a stable ref
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput(""); setBusy(true);
    try {
      const endpoint = mode === "homework" ? "/api/tutor/homework" : "/api/tutor/chat";
      const body = mode === "homework"
        ? { grade, subject, question: userMsg.content, session_id: sessionId }
        : { grade, subject, message: userMsg.content, session_id: sessionId };
      const { data } = await api.post(endpoint, body);
      setSessionId(data.session_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Tutor is unavailable.";
      toast.error(msg);
      if (e?.response?.status === 402) nav("/tutor/pricing");
    } finally { setBusy(false); }
  };

  if (!profile) return <div className="p-4" data-testid="tutor-loading">Loading…</div>;

  const Icon = mode === "homework" ? HelpCircle : MessageSquare;
  const title = mode === "homework" ? "Homework helper" : "AI Tutor chat";
  const subtitle = mode === "homework"
    ? "I won't give the answer — I'll guide you with hints."
    : "Ask anything — full explanations, worked examples.";

  return (
    <div data-testid={`tutor-${mode}`} className="flex flex-col h-[calc(100vh-180px)]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Icon className="w-3 h-3 text-[#D92D20]" /> Teacher4Me</div>
          <h1 className="font-display font-black text-3xl mt-1">{title}</h1>
          <p className="text-sm text-[#4B5563]">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="chat-grade" value={grade} onChange={(e) => setGrade(e.target.value)}
            className="border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-sm">
            {opts.grades.map((g) => <option key={g}>{g}</option>)}
          </select>
          <select data-testid="chat-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-sm">
            {opts.subjects.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div ref={scrollRef} data-testid="chat-window"
        className="flex-1 mt-4 overflow-y-auto bg-white border border-[#E5E7EB] rounded-2xl p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-sm text-[#4B5563]" data-testid="chat-empty">
            Try: &quot;Explain photosynthesis with an example&quot; or paste your question here.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.id} data-testid={`msg-${m.role}-${i}`}
            className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-[#D92D20] text-white ml-auto"
                : "bg-[#F9FAFB] border border-[#E5E7EB]"
            }`}>{m.content}</div>
        ))}
        {busy && <div className="text-xs text-[#4B5563]" data-testid="chat-typing">Tutor is typing…</div>}
      </div>

      <div className="mt-3 flex gap-2">
        <input data-testid="chat-input" value={input}
          onKeyDown={(e) => e.key === "Enter" && !busy && send()}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="flex-1 border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm" />
        <button data-testid="chat-send" onClick={send} disabled={busy || !input.trim()}
          className="btn-primary disabled:opacity-50 inline-flex items-center gap-1">
          <Send className="w-4 h-4" /> Send
        </button>
      </div>
    </div>
  );
}
